import { useEffect } from 'react';
import * as Checkbox from '@radix-ui/react-checkbox';
import { useForm } from 'react-hook-form';

import { ProcessStepDTO } from '@/types/api-types';

import IconClose from '@/assets/svg/icon-close.svg?react';
import IconMinus from '@/assets/svg/icon-minus.svg?react';
import IconPlus from '@/assets/svg/icon-plus.svg?react';

import { cn } from '@/lib/utils';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { USER_ROLES } from '@/constants/global-constants';

import { Input } from './ui/input';

interface FormValues extends Partial<ProcessStepDTO> {
  removeDaysUntilDeadline: boolean;
}

interface AddStepDialogProps {
  open: boolean;
  editingStep: ProcessStepDTO | null;
  onClose: () => void;
  onSubmit: (data: Partial<ProcessStepDTO>) => void;
}

export function AddStepDialog({
  open,
  editingStep,
  onClose,
  onSubmit,
}: AddStepDialogProps) {
  const form = useForm<FormValues>({
    defaultValues: {
      days_until_deadline: 0,
    },
  });

  useEffect(() => {
    if (editingStep) {
      form.reset({
        uuid: editingStep.uuid,
        status_name: editingStep.status_name,
        days_until_deadline: editingStep.days_until_deadline,
        removeDaysUntilDeadline: editingStep.days_until_deadline > 0,
        user_type: editingStep.user_type,
        comment: editingStep.comment,
        process_set: editingStep.process_set,
      });
    }
  }, [editingStep, form]);

  const handleClose = () => {
    form.reset({});
    onClose();
  };

  const handleSubmit = (data: FormValues) => {
    onSubmit({
      ...data,
      days_until_deadline: !data.removeDaysUntilDeadline
        ? 0
        : data.days_until_deadline,
    });
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="w-[432px] p-0">
        <DialogHeader className="p-5 pb-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-montserrat text-2xl font-semibold text-primary">
              Етап
            </DialogTitle>
          </div>
        </DialogHeader>

        <DialogDescription hidden>Етап</DialogDescription>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4 p-4"
          >
            <FormField
              control={form.control}
              name="status_name"
              rules={{ required: "Назва етапу є обов'язковою" }}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Назва етапу"
                      className="mb-6"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="user_type"
              rules={{ required: "Тип користувача є обов'язковим" }}
              render={({ field }) => (
                <FormItem>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger className="h-[50px] rounded-md border-separator">
                        <SelectValue placeholder="Тип користувача" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(USER_ROLES)
                        .filter(
                          (userType) =>
                            userType.value !== USER_ROLES.ADMIN.value,
                        )
                        .map((userType) => (
                          <SelectItem
                            value={userType.value}
                            key={userType.value}
                          >
                            <span>{userType.title}</span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <div className="flex items-center justify-between gap-2">
              <FormField
                control={form.control}
                name="removeDaysUntilDeadline"
                render={({ field }) => (
                  <Checkbox.Root
                    className="flex h-[50px] w-[50px] items-center justify-center rounded-md border border-gray-200"
                    onCheckedChange={field.onChange}
                    checked={field.value}
                  >
                    <Checkbox.Indicator>
                      <IconClose className="h-[20px] w-[20px] fill-primary" />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                )}
              />

              <FormField
                control={form.control}
                name="days_until_deadline"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <div className="relative flex items-center">
                      <input
                        {...field}
                        type="text"
                        placeholder="0"
                        className="h-[50px] w-full rounded-md border border-gray-200 border-separator pl-[230px] pr-[70px] outline-none disabled:text-gray-200"
                        onChange={(e) => {
                          const value = e.target.value;

                          if (/^\d{0,3}$/.test(value)) {
                            field.onChange(
                              value === '' ? undefined : Number(value),
                            );
                          }
                        }}
                        disabled={!form.watch('removeDaysUntilDeadline')}
                        defaultValue={field.value}
                      />
                      <span
                        className={cn(
                          'absolute left-4',
                          form.watch('removeDaysUntilDeadline')
                            ? 'text-primary'
                            : 'text-gray-200',
                        )}
                      >
                        К-сть днів на виконання:
                      </span>
                      <div className="absolute right-1 flex items-center text-gray-200">
                        <button
                          className="mr-[1px] flex h-[30px] w-[30px] items-center justify-center border-r border-gray-200 p-1 pr-[5px] hover:bg-gray-100"
                          type="button"
                          onClick={() => {
                            Number(field.value) > 0 &&
                              field.onChange(Number(field.value) - 1);
                          }}
                          disabled={!form.watch('removeDaysUntilDeadline')}
                        >
                          <IconMinus className="h-[16px] w-[16px]" />
                        </button>
                        <button
                          className="flex h-[30px] w-[30px] items-center justify-center p-1 hover:bg-gray-100"
                          type="button"
                          onClick={() => {
                            field.onChange(Number(field.value) + 1);
                          }}
                          disabled={!form.watch('removeDaysUntilDeadline')}
                        >
                          <IconPlus className="h-[16px] w-[16px]" />
                        </button>
                      </div>
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem>
                  <Textarea
                    {...field}
                    placeholder="Коментар"
                    className="min-h-[175px] resize-none rounded-md border-separator"
                  />
                </FormItem>
              )}
            />

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                className="h-[50px] w-[150px] rounded-md border border-primary bg-transparent text-primary hover:bg-primary hover:text-white"
                disabled={!form.formState.isValid}
              >
                {editingStep ? 'Змінити' : 'Додати'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
